import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useGlobalProgressStore } from '@/stores/useGlobalProgressStore';
import { RobustProgressBar } from './RobustProgressBar';

interface GlobalProgressProviderProps {
  children: React.ReactNode;
}

export const GlobalProgressProvider: React.FC<GlobalProgressProviderProps> = ({ children }) => {
  const { activeProgress } = useGlobalProgressStore();
  const location = useLocation();

  // Check if we're on a collection detail page (pattern: /collections/:collectionId)
  const isCollectionDetailPage = /^\/collections\/[^\/]+$/.test(location.pathname);
  const urlCollectionId = isCollectionDetailPage ? location.pathname.split('/')[2] : null;

  // Render active progress bars for all collections
  const activeCollectionIds = Object.keys(activeProgress).filter(collectionId => 
    useGlobalProgressStore.getState().shouldShowProgress(collectionId)
  );

  // Don't show floating progress bars on collection detail pages for the current collection
  // But allow floating bars for other collections if they're running in background
  const filteredCollectionIds = isCollectionDetailPage 
    ? activeCollectionIds.filter(id => id !== urlCollectionId)
    : activeCollectionIds;
    
  const shouldShowFloatingBars = filteredCollectionIds.length > 0;

  return (
    <>
      {children}
      
      {/* Render floating progress bars for active collections */}
      {shouldShowFloatingBars && filteredCollectionIds.map((collectionId, index) => (
        <div
          key={`global-progress-${collectionId}`}
          className="fixed right-4 w-96 z-50"
          style={{ 
            bottom: `${20 + (index * 180)}px` // Stack multiple progress bars
          }}
        >
          <RobustProgressBar
            collectionId={collectionId}
            className="bg-white border shadow-lg rounded-lg p-4"
            autoHide={false}
            onComplete={() => {
              // Auto-clear after completion
              setTimeout(() => {
                useGlobalProgressStore.getState().clearProgress(collectionId);
              }, 5000);
            }}
          />
        </div>
      ))}
    </>
  );
};